/**
 * Ruler tick and label math. Steps are clock-friendly (15 s, 30 s, 1 min, …)
 * rather than d3's 1/2/5 decades, and every tick is `index × step` so labels
 * never inherit float accumulation.
 */

// 0.01 and 0.02 only ever serve as minor steps at the maximum zoom.
const SECOND_STEPS = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30];
const MINUTE_STEPS = [1, 2, 5, 10, 15, 30].map((minutes) => minutes * 60);
const HOUR_STEPS = [1, 2, 3, 6, 12, 24].map((hours) => hours * 3600);
const RULER_STEPS: readonly number[] = [...SECOND_STEPS, ...MINUTE_STEPS, ...HOUR_STEPS];

const EPSILON = 1e-9;

/** Step whose on-screen spacing is closest to `targetPx`, never closer than `minPx`. */
export function majorTickStep(pixelsPerSecond: number, targetPx: number, minPx: number): number {
  let best = RULER_STEPS[RULER_STEPS.length - 1]!;
  let bestDistance = Infinity;
  for (const step of RULER_STEPS) {
    const spacing = step * pixelsPerSecond;
    if (spacing < minPx) continue;
    const distance = Math.abs(Math.log(spacing / targetPx));
    if (distance < bestDistance) {
      best = step;
      bestDistance = distance;
    }
  }
  return best;
}

/** Finest ruler step that evenly divides `major` and keeps ticks at least `minPx` apart. */
export function minorTickStep(major: number, pixelsPerSecond: number, minPx: number): number | null {
  for (const step of RULER_STEPS) {
    if (step >= major) break;
    const ratio = major / step;
    if (step * pixelsPerSecond >= minPx && Math.abs(ratio - Math.round(ratio)) < EPSILON) return step;
  }
  return null;
}

export type RulerTicks = {
  majorStep: number;
  major: { index: number; time: number }[];
  minor: number[];
};

export function rulerTicks({
  startSeconds,
  endSeconds,
  durationSeconds,
  pixelsPerSecond,
  targetSpacingPx,
  minMajorSpacingPx,
  minMinorSpacingPx,
}: {
  startSeconds: number;
  endSeconds: number;
  durationSeconds: number;
  pixelsPerSecond: number;
  targetSpacingPx: number;
  minMajorSpacingPx: number;
  minMinorSpacingPx: number;
}): RulerTicks {
  const majorStep = majorTickStep(pixelsPerSecond, targetSpacingPx, minMajorSpacingPx);
  const from = Math.max(0, startSeconds);
  const to = Math.min(durationSeconds, endSeconds);
  const major: RulerTicks["major"] = [];
  const minor: number[] = [];
  if (!(to >= from)) return { majorStep, major, minor };

  // Math.max also turns Math.ceil's -0 into 0.
  for (let index = Math.max(0, Math.ceil(from / majorStep - EPSILON)); index * majorStep <= to + EPSILON; index++) {
    major.push({ index, time: index * majorStep });
  }

  const minorStep = minorTickStep(majorStep, pixelsPerSecond, minMinorSpacingPx);
  if (minorStep !== null) {
    const perMajor = Math.round(majorStep / minorStep);
    for (let index = Math.max(0, Math.ceil(from / minorStep - EPSILON)); index * minorStep <= to + EPSILON; index++) {
      if (index % perMajor !== 0) minor.push(index * minorStep);
    }
  }
  return { majorStep, major, minor };
}

/** Decimal places a label needs so neighbouring ticks never share text. */
export function labelDecimals(step: number): number {
  if (step >= 1) return 0;
  return step >= 0.1 ? 1 : 2;
}

/**
 * `mm:ss`, `mm:ss.s` or `h:mm:ss` with rounding carried through every field,
 * so 59.96 s at one decimal reads `01:00.0`, not `00:59.0`.
 */
export function formatTimelineTime(time: number, decimals: number, showHours: boolean): string {
  const scale = 10 ** decimals;
  const units = Math.round(Math.max(0, time) * scale);
  const totalSeconds = Math.floor(units / scale);
  const fraction = decimals > 0 ? `.${String(units % scale).padStart(decimals, "0")}` : "";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  if (showHours) return `${hours}:${String(minutes).padStart(2, "0")}:${seconds}${fraction}`;
  return `${String(Math.floor(totalSeconds / 60)).padStart(2, "0")}:${seconds}${fraction}`;
}

/** Every time label for a video uses hours once the video is an hour or longer, so they all agree. */
export function showsHours(durationSeconds: number): boolean {
  return durationSeconds >= 3600;
}

/** The time under the playhead or pointer: tenths, or hundredths once zoomed in to frame level. */
export function formatPointerTime(time: number, pixelsPerSecond: number, durationSeconds: number): string {
  return formatTimelineTime(time, pixelsPerSecond >= 200 ? 2 : 1, showsHours(durationSeconds));
}
